/**
 * Simplified Supplier Form - 2 Tabs Only
 * Easy supplier onboarding with minimal required fields
 */

import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box, Paper, Typography, TextField, Grid, FormControlLabel,
  Switch, Tabs, Tab,
} from '@mui/material'
import {
  Save as SaveIcon, Business as BusinessIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import PageHeader from '../components/ui/PageHeader'
import { PurchaseService } from '../services/purchaseService'

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface Address {
  line1: string
  line2?: string
  city: string
  state: string
  pincode: string
  country: string
}

interface SupplierFormData {
  business_name: string
  contact_person?: string
  phone: string
  email?: string
  gstin?: string
  address: Address
  payment_terms: number
  is_active: boolean
}

const SupplierFormSimple: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { enqueueSnackbar } = useSnackbar()
  const isEditing = Boolean(id)

  const [activeTab, setActiveTab] = useState(0)

  const [formData, setFormData] = useState<SupplierFormData>({
    business_name: '',
    contact_person: '',
    phone: '',
    email: '',
    gstin: '',
    address: {
      line1: '',
      line2: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
    },
    payment_terms: 30,
    is_active: true,
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isEditing && id) {
      loadSupplier(id)
    }
  }, [id, isEditing])

  const loadSupplier = async (supplierId: string) => {
    try {
      setLoading(true)
      const supplier = await PurchaseService.getSupplier(supplierId)
      setFormData({
        business_name: supplier.businessName || '',
        contact_person: supplier.contactPerson || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        gstin: supplier.gstin || '',
        address: supplier.address || formData.address,
        payment_terms: supplier.paymentTerms || 30,
        is_active: supplier.isActive !== undefined ? supplier.isActive : true,
      })
    } catch (error: any) {
      enqueueSnackbar(
        error?.response?.data?.detail || 'Failed to load supplier',
        { variant: 'error' }
      )
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: keyof SupplierFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }))
  }

  const handleAddressChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      address: {
        ...prev.address,
        [field]: value,
      },
    }))
  }

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    // Required fields
    if (!formData.business_name.trim()) newErrors.business_name = 'Business name is required'
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required'

    // Phone validation
    if (formData.phone && !/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number (10 digits starting with 6-9)'
    }

    // Email validation (if provided)
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address'
    }

    // GSTIN validation (if provided)
    if (formData.gstin && formData.gstin.length !== 15) {
      newErrors.gstin = 'GSTIN must be 15 characters'
    }

    // Address validation
    if (!formData.address.line1.trim()) newErrors['address.line1'] = 'Address is required'
    if (!formData.address.city.trim()) newErrors['address.city'] = 'City is required'
    if (!formData.address.state.trim()) newErrors['address.state'] = 'State is required'
    if (!formData.address.pincode.trim()) {
      newErrors['address.pincode'] = 'Pincode is required'
    } else if (!/^\d{6}$/.test(formData.address.pincode)) {
      newErrors['address.pincode'] = 'Pincode must be 6 digits'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) {
      enqueueSnackbar('Please fix the errors in the form', { variant: 'error' })
      return
    }

    setLoading(true)
    try {
      const payload = {
        businessName: formData.business_name,
        contactPerson: formData.contact_person || undefined,
        phone: formData.phone,
        email: formData.email || undefined,
        gstin: formData.gstin || undefined,
        address: formData.address,
        paymentTerms: formData.payment_terms,
        supplierMetadata: {},
      }

      if (isEditing) {
        await PurchaseService.updateSupplier(id!, { ...payload, isActive: formData.is_active })
        enqueueSnackbar('Supplier updated successfully', { variant: 'success' })
      } else {
        await PurchaseService.createSupplier(payload)
        enqueueSnackbar('Supplier created successfully', { variant: 'success' })
      }
      navigate('/admin/parties/suppliers')
    } catch (error: any) {
      enqueueSnackbar(error?.response?.data?.detail || 'Failed to save supplier', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (loading && isEditing) {
    return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>
  }

  return (
    <Box>
      <PageHeader
        title={isEditing ? 'Edit Supplier' : 'Add Supplier'}
        subtitle="Simple form with essential information only"
        breadcrumbs={[
          { label: 'Party Management', path: '/admin/parties' },
          { label: 'Suppliers', path: '/admin/parties/suppliers' },
          { label: isEditing ? 'Edit' : 'Add', path: '' }
        ]}
        actions={[
          {
            label: loading ? 'Saving...' : 'Save Supplier',
            onClick: handleSubmit,
            variant: 'contained' as const,
            icon: <SaveIcon />,
            disabled: loading,
          }
        ]}
      />

      <Paper sx={{ mt: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<BusinessIcon />} label="Basic Information" iconPosition="start" />
          <Tab icon={<LocationIcon />} label="Address & Terms" iconPosition="start" />
        </Tabs>

        {/* Tab 1: Basic Information */}
        <TabPanel value={activeTab} index={0}>
          <Grid container spacing={3}>
            {/* Business Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Business Name"
                value={formData.business_name}
                onChange={(e) => handleInputChange('business_name', e.target.value)}
                error={Boolean(errors.business_name)}
                helperText={errors.business_name || 'Legal business name'}
              />
            </Grid>

            {/* Contact Person */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Contact Person"
                value={formData.contact_person}
                onChange={(e) => handleInputChange('contact_person', e.target.value)}
                helperText="Primary contact person name (optional)"
              />
            </Grid>

            {/* Phone */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                error={Boolean(errors.phone)}
                helperText={errors.phone || '10-digit mobile number'}
                placeholder="9876543210"
              />
            </Grid>

            {/* Email */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={Boolean(errors.email)}
                helperText={errors.email || 'Email address (optional)'}
              />
            </Grid>

            {/* GSTIN */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="GSTIN"
                value={formData.gstin}
                onChange={(e) => handleInputChange('gstin', e.target.value.toUpperCase())}
                error={Boolean(errors.gstin)}
                helperText={errors.gstin || '15-character GST number (optional)'}
                placeholder="22AAAAA0000A1Z5"
              />
            </Grid>

            {/* Payment Terms */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Payment Terms (Days)"
                type="number"
                value={formData.payment_terms}
                onChange={(e) => handleInputChange('payment_terms', parseInt(e.target.value) || 0)}
                helperText="Number of days for payment (default: 30)"
                inputProps={{ min: 0, max: 365 }}
              />
            </Grid>

            {/* Active Status (only for editing) */}
            {isEditing && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    />
                  }
                  label="Active Supplier"
                />
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 2: Address & Terms */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Business Address
              </Typography>
            </Grid>

            {/* Address Line 1 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Address Line 1"
                value={formData.address.line1}
                onChange={(e) => handleAddressChange('line1', e.target.value)}
                error={Boolean(errors['address.line1'])}
                helperText={errors['address.line1'] || 'Street address, building name'}
              />
            </Grid>

            {/* Address Line 2 */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={formData.address.line2}
                onChange={(e) => handleAddressChange('line2', e.target.value)}
                helperText="Landmark, area (optional)"
              />
            </Grid>

            {/* City */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="City"
                value={formData.address.city}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                error={Boolean(errors['address.city'])}
                helperText={errors['address.city']}
              />
            </Grid>

            {/* State */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="State"
                value={formData.address.state}
                onChange={(e) => handleAddressChange('state', e.target.value)}
                error={Boolean(errors['address.state'])}
                helperText={errors['address.state']}
              />
            </Grid>

            {/* Pincode */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Pincode"
                value={formData.address.pincode}
                onChange={(e) => handleAddressChange('pincode', e.target.value)}
                error={Boolean(errors['address.pincode'])}
                helperText={errors['address.pincode'] || '6-digit pincode'}
                placeholder="400001"
              />
            </Grid>

            {/* Country */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.address.country}
                onChange={(e) => handleAddressChange('country', e.target.value)}
                disabled
                helperText="Currently only India is supported"
              />
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  )
}

export default SupplierFormSimple
